import React, { useState, useRef, useCallback, useEffect } from "react";
import HeaderDocument from "./HeaderDocument";
import VideoModal from "../tutorials/VideoModal";
import VideoThumbnail from "../tutorials/VideoThumbnail";
import Layout from "../Layout";
import { row1Videos, row2Videos, row3Videos, row4Videos } from "../tutorials/VideoData";
import CallAPI from "../../services/apiCalls";
import useUserStore from "../../utils/storeApp";

const Documents = () => {
    const { userToken } = useUserStore((state) => ({
        userToken: state.userToken,
    }));

    const [openModal, setOpenModal] = useState(false);
    const [currentVideoTitle, setCurrentVideoTitle] = useState("");
    const videoButtonRefs = useRef({});
    const [videoUrl, setVideoUrl] = useState("");

    const row1Ref = useRef(null);
    const row2Ref = useRef(null);
    const row3Ref = useRef(null);
    const row4Ref = useRef(null);

    // Cache for signed URLs to avoid repeated backend calls for thumbnails
    const signedUrlCache = useRef(new Map());

    const scrollCarousel = (ref, direction) => {
        if (ref.current) {
            const scrollAmount = ref.current.offsetWidth * 0.8;
            ref.current.scrollBy({
                left: direction === "left" ? -scrollAmount : scrollAmount,
                behavior: "smooth",
            });
        }
    };

    const fetchSignedUrl = useCallback(async (videoKey) => {
        if (!videoKey) return "";
        const cache = signedUrlCache.current;
        if (cache.has(videoKey)) return cache.get(videoKey);

        try {
            const call = `/videos/link?videoKey=${encodeURIComponent(videoKey)}`;
            const res = await CallAPI(call, false, "get", userToken);
            const signed = res?.data?.url ?? res?.data ?? "";
            if (signed) cache.set(videoKey, signed);
            return signed;
        } catch (ex) {
            return "";
        }
    }, [userToken]);

    const fetchBatchSignedUrls = useCallback(async (videoKeys) => {
        if (!videoKeys || videoKeys.length === 0) return;
        const cache = signedUrlCache.current;
        const keysToRequest = videoKeys.filter(k => k && !cache.has(k));
        if (keysToRequest.length === 0) return;

        try {
            const call = `/videos/links`;
            const body = { videoKeys: keysToRequest };
            const res = await CallAPI(call, body, "post", userToken, false);
            const links = res?.data?.links ?? res?.data ?? null;
            if (links && typeof links === "object") {
                Object.keys(links).forEach((k) => {
                    const v = links[k];
                    if (v) cache.set(k, v);
                });
            }
        } catch (ex) {
            console.warn("Batch signed-url fetch failed", ex);
        }
    }, [userToken]);

    useEffect(() => {
        const allRows = [...row1Videos, ...row2Videos, ...row3Videos, ...row4Videos];
        const allKeys = allRows
            .map(v => v.videoKey ?? "")
            .filter(Boolean);
        if (allKeys.length > 0) {
            fetchBatchSignedUrls(Array.from(new Set(allKeys)));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchSignedDocumentUrl = useCallback(async (objectKey) => {
        if (!objectKey) return "";
        try {
            const call = `/documents/link?objectKey=${encodeURIComponent(objectKey)}`;
            const res = await CallAPI(call, false, "get", userToken);
            const signed = res?.data?.url ?? res?.data ?? "";
            return signed;
        } catch (ex) {
            return "";
        }
    }, [userToken]);

    const openDocument = async (objectKey, fallbackUrl) => {
        let downloadUrl = "";
        if (objectKey) {
            const signed = await fetchSignedDocumentUrl(objectKey);
            if (signed) {
                downloadUrl = signed;
            }
        }
        if (!downloadUrl && fallbackUrl) {
            downloadUrl = fallbackUrl;
        }
        if (downloadUrl) {
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = ""; 
            link.style.display = "none";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setVideoUrl("");
        // Return focus to the thumbnail button that opened
        // the modal so keyboard users don't lose their place.
        if (currentVideoTitle && videoButtonRefs.current[currentVideoTitle]) {
            videoButtonRefs.current[currentVideoTitle].focus();
        }
    };

    const renderCarousel = (videos, ref, label) => (
        <div className="carousel-wrapper">
            <button
                type="button"
                onClick={() => scrollCarousel(ref, "left")}
                className="carousel-scroll-left"
                aria-label={`Scroll ${label} left`}
            >
                {<i className="bi bi-chevron-compact-left" aria-hidden="true"></i>}
            </button>

            <div
                className="video-carousel"
                ref={ref}
                role="region"
                aria-label={`${label} carousel`}
                tabIndex="0"
            >
                {videos.map((video) => (
                    <div key={video.id} className="video-item dark-video" style={{ flex: "0 0 auto", minWidth: "210px" }}>
                        <VideoThumbnail
                            thumbnail={video.thumbnail}
                            aria-label={`Play video: ${video.title || "untitled"}`}
                            ref={(el) => { if (el) videoButtonRefs.current[video.title] = el; }}
                            onClick={async () => {
                                setCurrentVideoTitle(video.title);
                                if (video.videoKey) {
                                    const signed = await fetchSignedUrl(video.videoKey);
                                    if (signed) {
                                        setVideoUrl(signed);
                                        setOpenModal(true);
                                    } else {
                                        setVideoUrl("");
                                    }
                                } else if (video.url) {
                                    setVideoUrl(video.url);
                                    setOpenModal(true);
                                } else {
                                    setVideoUrl("");
                                }
                            }}
                        />
                        <div className="video-info">
                            <h4 className="video-title">{video.title}</h4>
                            <p className="video-date">
                                <time>{video.date}</time>
                            </p>
                            <p className="video-caption">{video.caption}</p>
                        </div>
                    </div>
                ))}
            </div>

            <button
                type="button"
                onClick={() => scrollCarousel(ref, "right")}
                className="carousel-scroll-right"
                aria-label={`Scroll ${label} right`}
            >
                {<i className="bi bi-chevron-compact-right" aria-hidden="true"></i>}
            </button>
        </div>
    );

    return (
        <Layout>
            <HeaderDocument />
            <main>
                <section className="layout-container">
                <div className="container">
                    <section className="left-panel-docs" aria-labelledby="apiDocsHeading" >
                        <h2 className="form-title g-clr" id="apiDocsHeading">API</h2>
                        <ul className="spacer-margin4 indent-r">
                            <li>
                                <a href="https://svr-registration.directaddress.net/RA_v2/Help" target="_blank" rel="noreferrer" >
                                    Registration API v2 Endpoint
                                </a>
                            </li>
                            <li>
                                <button
                                    onClick={async () => await openDocument("Registration_API_v2.pdf", "https://sesportalresources.s3.us-east-1.amazonaws.com/Registration_API_v2.pdf")}
                                    className="btn-link bg-1 bdr-1 sp-pad0"
                                >
                                    Registration API v2 Documentation (pdf)
                                    <span className="sr-only">(opens in a new tab)</span>
                                </button>
                            </li>
                            <li>
                                <button
                                    type="button"
                                    onClick={async () => await openDocument("SES_Reporting_API_v1.pdf", "https://sesportalresources.s3.us-east-1.amazonaws.com/SES_Reporting_API_v1.pdf")}
                                    className="btn-link bg-1 bdr-1 sp-pad0"
                                    aria-label="Download Reporting API v1 Documentation (PDF)"
                                >
                                    Reporting API v1 Documentation (pdf)
                                </button>
                            </li>
                            <li>
                                <button
                                    type="button"
                                    onClick={async () => await openDocument("SES%20Connect%20v2.5%20Web%20Service%20API.pdf", "https://sesportalresources.s3.us-east-1.amazonaws.com/SES%20Connect%20v2.5%20Web%20Service%20API.pdf")}
                                    className="btn-link bg-1 bdr-1 sp-pad0"
                                    aria-label="Download CHSConnect v2.5 Web Service API (PDF)"
                                >
                                    CHSConnect v2 5 Web Service API (pdf)
                                </button>
                            </li>
                            <li>
                                <button
                                    type="button"
                                    onClick={async () => await openDocument("RegAPI-Client-master.zip", "https://sesportalresources.s3.us-east-1.amazonaws.com/RegAPI-Client-master.zip")}
                                    className="btn-link bg-1 bdr-1 sp-pad0"
                                    aria-label="Download Registration API v2 Sample Code (ZIP)"
                                >
                                    Registration API v2 Sample Code (zip)
                                </button>
                            </li>
                            <li>
                                <button
                                    type="button"
                                    onClick={async () => await openDocument("TranportSecurityClientSampleCode.zip", "https://sesportalresources.s3.us-east-1.amazonaws.com/TranportSecurityClientSampleCode.zip")}
                                    className="btn-link bg-1 bdr-1 sp-pad0"
                                    aria-label="Download Transport Security Client Sample Code (ZIP)"
                                >
                                    Transport Security Client SampleCode (zip)
                                </button>
                            </li>
                        </ul>
                    </section>
                    <section className="right-panel-docs" aria-labelledby="mediaHeading">
                        <h2 className="form-title g-clr spacer-margin3" id="mediaHeading">Media</h2>
                        <div>
                            <section aria-labelledby="webmailTutorialsHeading">
                                <header id="webmailTutorialsHeading">Webmail Tutorials </header>
                                {renderCarousel(row4Videos, row4Ref)}
                            </section>
                            <section aria-labelledby="orgMgmtTutorialsHeading">
                                <header id="orgMgmtTutorialsHeading">Organization Management Tutorials </header>
                                {renderCarousel(row1Videos, row1Ref)}
                            </section>
                            <section aria-labelledby="userMgmtTutorialsHeading">
                                <header id="userMgmtTutorialsHeading">User Management Tutorials</header>
                                {renderCarousel(row2Videos, row2Ref)}
                            </section>
                            <section aria-labelledby="reportsTutorialsHeading">
                                <header id="reportsTutorialsHeading">Reports Tutorials</header>
                                {renderCarousel(row3Videos, row3Ref)}
                            </section>

                            <VideoModal
                                open={openModal}
                                onClose={() => {
                                    setOpenModal(false);
                                    setVideoUrl("");
                                }}
                                videoUrl={videoUrl}
                                title={currentVideoTitle}
                            />
                        </div>
                    </section>
                </div>
            </section>
        </main>
            <VideoModal
                open={openModal}
                onClose={handleCloseModal}
                videoUrl={videoUrl}
            />
        </Layout>
    );
};

export default Documents;